import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Table, Modal, Collapse } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import {
  getPurchaseOrderApi,
  updatePurchaseOrderApi,
  confirmPurchaseOrderApi,
  cancelPurchaseOrderApi,
  getPurchaseOrderDocumentApi,
  uniformErrorMessage,
} from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';
import DocumentAttachment from '../../components/DocumentAttachment';
import POFormModal from './POFormModal';
import ReceiveStockModal from './ReceiveStockModal';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

const PO_LIST_PATH = '/uniform-management/purchase-orders';

/**
 * Receipts are hidden on the record page for now — a frontend-only decision
 * (docs/uniform-ux-backlog.md, item 4). The API still returns `po.receipts`
 * and the rendering below is intact: flip this to `true` to bring the section
 * back. Nothing on the backend was changed and nothing else reads this flag.
 */
const SHOW_RECEIPTS = false;

/** One fact in the meta strip. `wide` spans the full grid row (remarks). */
const Meta = ({ label, wide, children }) => (
  <div className={wide ? 'detail-meta-item detail-meta-item--wide' : 'detail-meta-item'}>
    <div className="detail-meta-label">{label}</div>
    <div className="detail-meta-value">{children || '—'}</div>
  </div>
);

const PODetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // rendering unthemed.
  const [modal, modalContextHolder] = Modal.useModal();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);

  const fetchData = () => {
    setLoading(true);
    getPurchaseOrderApi(id).then((response) => {
      setPo(response.data?.data || null);
      setLoading(false);
    }).catch((e) => {
      setLoading(false);
      toast.error(uniformErrorMessage(e, 'Failed to load purchase order'), toastOpts);
      navigate(PO_LIST_PATH);
    });
  };

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only the PO/LPO attachment is editable from this page now; the invoice
  // fields are hidden here (item 4) but remain on the model and the endpoint,
  // so the payload stays scoped to the one document it owns.
  const handleDocumentChange = (value) => {
    updatePurchaseOrderApi(id, {
      po_document_url: value?.document_url || '',
      po_document_name: value?.document_name || '',
    }).then(() => {
      toast.success('Document updated', toastOpts);
      fetchData();
    }).catch((e) => toast.error(uniformErrorMessage(e, 'Failed to update document'), toastOpts));
  };

  const handleConfirm = () => {
    setActionLoading(true);
    confirmPurchaseOrderApi(id).then(() => {
      toast.success('Purchase order confirmed', toastOpts);
      setActionLoading(false);
      fetchData();
    }).catch((e) => {
      setActionLoading(false);
      toast.error(uniformErrorMessage(e, 'Failed to confirm purchase order'), toastOpts);
    });
  };

  const handleCancel = () => {
    modal.confirm({
      title: 'Cancel Purchase Order',
      centered: true,
      content: 'Are you sure you want to cancel this purchase order? This cannot be undone.',
      okText: 'Yes, Cancel',
      okType: 'danger',
      onOk: () => {
        setActionLoading(true);
        cancelPurchaseOrderApi(id).then(() => {
          toast.success('Purchase order cancelled', toastOpts);
          setActionLoading(false);
          fetchData();
        }).catch((e) => {
          setActionLoading(false);
          toast.error(uniformErrorMessage(e, 'Failed to cancel purchase order'), toastOpts);
        });
      }
    });
  };

  if (loading || !po) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const documentsDisabled = po.status === 'CANCELLED';
  const canEdit = po.status === 'DRAFT';
  const canConfirm = po.status === 'DRAFT';
  const canCancel = (po.status === 'DRAFT') || (po.status === 'OPEN' && Number(po.received_total) === 0);
  const canReceive = po.status === 'OPEN' || po.status === 'PARTIALLY_RECEIVED';

  // Colour only. `getStatusBadge` also returns a 4px radius and 12px/500 type,
  // and an inline style beats a class — so the pill geometry would be the
  // constant's, not `.detail-status`'s.
  const { backgroundColor, color, border } = getStatusBadge(po.status);
  const statusColors = { backgroundColor, color, border };

  // Annotated on the detail queryset (uniform/views/purchase_order.py), so the
  // totals are the server's sums, not a client re-add of the visible rows.
  const orderedTotal = Number(po.ordered_total) || 0;
  const receivedTotal = Number(po.received_total) || 0;
  const balanceTotal = orderedTotal - receivedTotal;

  const lineColumns = [
    { title: 'Item', dataIndex: 'uniform_item_name' },
    {
      title: 'Size',
      render: (_, record) => record.size ? (
        <>
          <span style={{ fontWeight: 500 }}>{record.size_code}</span>
          <span className="text-muted" style={{ marginLeft: 6, fontSize: 13 }}>{record.size_name}</span>
        </>
      ) : '—'
    },
    { title: 'Ordered', dataIndex: 'ordered_quantity', align: 'right', width: 110, className: 'detail-num' },
    { title: 'Received', dataIndex: 'received_quantity', align: 'right', width: 110, className: 'detail-num' },
    {
      title: 'Balance', dataIndex: 'remaining_quantity', align: 'right', width: 110, className: 'detail-num',
      render: (v) => {
        // A closed PO that still owes stock is the one number on this page
        // worth shouting about.
        const flag = v > 0 && po.status === 'RECEIVED';
        return <span style={flag ? { color: 'var(--brand-primary)', fontWeight: 700 } : undefined}>{v}</span>;
      }
    },
  ];

  const receiptLineColumns = [
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code', render: (v) => v || '—' },
    { title: 'Qty', dataIndex: 'quantity', align: 'right', className: 'detail-num' },
    { title: 'Remarks', dataIndex: 'remarks', render: (v) => v || '—' },
  ];

  const receiptItems = (po.receipts || []).map((r) => ({
    key: r.id,
    label: (
      <div className="d-flex flex-wrap gap-3">
        <span><strong>{r.reference}</strong></span>
        <span className="text-muted">{formatDateHandler(r.received_date)}</span>
        <span className="text-muted">{r.warehouse_name}</span>
        <span className="text-muted">by {r.received_by_name}</span>
      </div>
    ),
    children: (
      <>
        {r.remarks && <div className="mb-2"><strong>Remarks:</strong> {r.remarks}</div>}
        <Table columns={receiptLineColumns} dataSource={r.lines || []} rowKey={(l) => l.id}
          pagination={false} size="small" scroll={{ x: 'max-content' }} />
      </>
    )
  }));

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {modalContextHolder}

        <Link to={PO_LIST_PATH} className="detail-back">
          <i className="fa fa-arrow-left" aria-hidden="true" />
          Purchase Orders
        </Link>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 mb-3">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h3 className="detail-title">{po.po_number}</h3>
            <span className="detail-status" style={statusColors}>
              {po.status.replace('_', ' ')}
            </span>
          </div>
          {/* Confirm and Receive Stock are mutually exclusive by status, so
              both can carry the single primary emphasis without ever competing.
              Cancel drops to a text action — it is destructive but rare, and it
              is confirmed anyway. */}
          <div className="detail-actions d-flex align-items-center flex-wrap gap-2">
            {canEdit && (
              <Button label="Edit" icon="fa fa-pencil" className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowEditModal(true)} />
            )}
            {canConfirm && (
              <Button label="Confirm" icon="fa fa-check" className="btn btn-primary btn-sm"
                loading={actionLoading} disabled={actionLoading} onClick={handleConfirm} />
            )}
            {canReceive && (
              <Button label="Receive Stock" icon="fa fa-truck" className="btn btn-primary btn-sm"
                onClick={() => setShowReceiveModal(true)} />
            )}
            {canCancel && (
              <Button label="Cancel" className="btn btn-link btn-sm detail-action-danger"
                loading={actionLoading} disabled={actionLoading} onClick={handleCancel} />
            )}
          </div>
        </div>

        <div className="detail-shell">
          <section className="detail-section">
            <div className="detail-meta">
              <Meta label="PO Date">{formatDateHandler(po.po_date)}</Meta>
              <Meta label="Expected Delivery">
                {po.expected_delivery_date ? formatDateHandler(po.expected_delivery_date) : null}
              </Meta>
              <Meta label="Supplier">{po.supplier_name}</Meta>
              <Meta label="Created By">{po.created_by_name}</Meta>
              {po.remarks && <Meta label="Remarks" wide>{po.remarks}</Meta>}
            </div>
          </section>

          <section className="detail-section">
            <div className="detail-section-head">
              <h4 className="detail-section-title">PO / LPO Document</h4>
            </div>
            <DocumentAttachment
              className="detail-document"
              value={po.po_document_url ? { document_url: po.po_document_url, document_name: po.po_document_name } : null}
              onChange={handleDocumentChange}
              fetchDocumentApi={(p) => getPurchaseOrderDocumentApi(po.id, 'po', p)}
              disabled={documentsDisabled} />
          </section>

          <section className="detail-section detail-section--flush">
            <div className="detail-section-head">
              <h4 className="detail-section-title">Lines</h4>
              <div className="detail-section-summary">
                <strong>{orderedTotal}</strong> ordered
                <span className="detail-sep" aria-hidden="true">·</span>
                <strong>{receivedTotal}</strong> received
                <span className="detail-sep" aria-hidden="true">·</span>
                <strong>{balanceTotal}</strong> balance
              </div>
            </div>
            <Table columns={lineColumns} dataSource={po.lines || []} rowKey={(r) => r.id}
              pagination={false} scroll={{ x: 'max-content' }} />
          </section>

          {SHOW_RECEIPTS && (
            <section className="detail-section">
              <div className="detail-section-head">
                <h4 className="detail-section-title">Receipts</h4>
              </div>
              {receiptItems.length ? (
                <Collapse items={receiptItems} />
              ) : (
                <div className="text-muted" style={{ fontSize: 13 }}>No stock received yet.</div>
              )}
            </section>
          )}
        </div>

        {showEditModal && (
          <POFormModal
            open={showEditModal}
            po={po}
            onClose={() => setShowEditModal(false)}
            onSaved={() => { setShowEditModal(false); fetchData(); }}
          />
        )}

        {showReceiveModal && (
          <ReceiveStockModal
            open={showReceiveModal}
            po={po}
            onClose={() => setShowReceiveModal(false)}
            onReceived={() => { setShowReceiveModal(false); fetchData(); }}
          />
        )}
      </div>
    </div>
  );
};

export default PODetail;
